import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateRecurringEntryInput,
  ListRecurringEntriesQuery,
  RecurringEntry,
  RecurringEntryWithLines,
  RunRecurringResult,
  UpdateRecurringEntryInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const recurringKey = (companyId: string | null, filters: Partial<ListRecurringEntriesQuery> = {}) =>
  ['recurring-entries', companyId ?? 'none', filters.isActive ?? 'any'] as const;

export function useRecurringEntries(companyId: string | null, filters: Partial<ListRecurringEntriesQuery> = {}) {
  return useQuery({
    queryKey: recurringKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.isActive) params.set('isActive', filters.isActive);
      const qs = params.toString();
      return api
        .get<{ recurringEntries: RecurringEntry[] }>(`/companies/${companyId}/recurring-entries${qs ? '?' + qs : ''}`)
        .then((r) => r.recurringEntries);
    },
  });
}

export function useRecurringEntry(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['recurring-entry', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ recurringEntry: RecurringEntryWithLines }>(`/companies/${companyId}/recurring-entries/${id}`)
        .then((r) => r.recurringEntry),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['recurring-entries', c] });
  qc.invalidateQueries({ queryKey: ['recurring-entry', c] });
}

function invalidateLedger(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateRecurringEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecurringEntryInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ recurringEntry: RecurringEntryWithLines }>(`/companies/${companyId}/recurring-entries`, input)
        .then((r) => r.recurringEntry);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateRecurringEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRecurringEntryInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .put<{ recurringEntry: RecurringEntryWithLines }>(`/companies/${companyId}/recurring-entries/${id}`, input)
        .then((r) => r.recurringEntry);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useSetRecurringActive(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ recurringEntry: RecurringEntry }>(`/companies/${companyId}/recurring-entries/${id}/active`, {
        isActive,
      });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRunRecurringEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: RunRecurringResult }>(`/companies/${companyId}/recurring-entries/${id}/run`, {})
        .then((r) => r.result);
    },
    onSuccess: () => {
      invalidate(qc, companyId);
      invalidateLedger(qc, companyId);
    },
  });
}

export function useDeleteRecurringEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<void>(`/companies/${companyId}/recurring-entries/${id}`);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
